import { Link } from "react-router-dom";
import { ArrowRight, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCorrelationMatrix } from "@/hooks/useCorrelationMatrix";
import { useMemo } from "react";

function fmt(v: number) {
  return (v >= 0 ? "" : "") + v.toFixed(2);
}

export const MarketAnalyticsFeature = () => {
  const { data: rows, isLoading } = useCorrelationMatrix("30d");

  const stats = useMemo(() => {
    if (!rows || rows.length === 0) return null;
    let hi = rows[0];
    let lo = rows[0];
    let sum = 0;
    for (const r of rows) {
      if (r.correlation > hi.correlation) hi = r;
      if (r.correlation < lo.correlation) lo = r;
      sum += r.correlation;
    }
    return { hi, lo, avg: sum / rows.length };
  }, [rows]);

  const avgTone = (v: number) =>
    v > 0.8 ? "text-destructive" : v >= 0.5 ? "text-warning" : "text-success";
  const avgSub = (v: number) =>
    v > 0.8
      ? "High — most assets moving in lockstep"
      : v >= 0.5
      ? "Moderate — market moving in loose alignment"
      : "Low — diversification opportunities present";

  return (
    <section className="border-y border-border/60 bg-card/20">
      <div className="container mx-auto px-4 py-12">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="w-5 h-5 text-primary" />
              <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
                Market Analytics Terminal
              </h2>
            </div>
            <p className="text-sm text-muted-foreground">
              Institutional-grade crypto analysis — updated daily
            </p>
          </div>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border/40 border border-border/40 mb-6">
            {[0, 1, 2].map((i) => (
              <div key={i} className="bg-card p-6 h-32 animate-pulse" />
            ))}
          </div>
        ) : !stats ? (
          <div className="border border-border/40 bg-card p-10 text-center text-muted-foreground mb-6">
            Analytics data is being computed. Check back shortly.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border/40 border border-border/40 mb-6">
            <div className="bg-card p-6">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
                Avg Market Correlation (30D)
              </div>
              <div className={`font-mono text-4xl font-bold ${avgTone(stats.avg)}`}>
                {fmt(stats.avg)}
              </div>
              <div className="text-xs text-muted-foreground mt-2">{avgSub(stats.avg)}</div>
            </div>
            <div className="bg-card p-6">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
                Most Independent Pair (30D)
              </div>
              <div className="font-mono text-2xl font-bold">
                {stats.lo.coin_a_symbol} ↔ {stats.lo.coin_b_symbol}
              </div>
              <div className="font-mono text-lg text-muted-foreground mt-1">
                {fmt(stats.lo.correlation)}
              </div>
              <div className="text-xs text-muted-foreground mt-2">
                Strongest diversification potential
              </div>
            </div>
            <div className="bg-card p-6">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
                Highest Correlation (30D)
              </div>
              <div className="font-mono text-2xl font-bold">
                {stats.hi.coin_a_symbol} ↔ {stats.hi.coin_b_symbol}
              </div>
              <div className="font-mono text-lg text-muted-foreground mt-1">
                {fmt(stats.hi.correlation)}
              </div>
              <div className="text-xs text-muted-foreground mt-2">
                Weakest diversification — near-identical movement
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-center">
          <Button asChild variant="default" className="gap-2">
            <Link to="/market-analytics">
              Open Analytics Terminal <ArrowRight className="w-4 h-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};
